import { Injectable } from '@angular/core';
import { AuthService } from 'app/modules/authentication/services/auth.service';
import { Rbac, RbacGeneration, RbacSpecificationVersion } from 'app/modules/shared/model/rbac';
import { ApiProxyService } from 'app/modules/shared/services/api-proxy.service';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { FileService } from './file.service';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class RbacService {

  public rbacItems: Rbac[] = [];

  constructor(private apiProxy: ApiProxyService,
    private authService: AuthService,
    private fileService: FileService,
    private configService: ConfigService) { }

  public get hasFecthedRbaccFiles(): boolean {
    return this.rbacItems && this.rbacItems.length > 0;
  }

  public getRbacs(): Observable<Rbac[]> {
    if (this.hasFecthedRbaccFiles) {
      return of(this.rbacItems);
    }

    return this.apiProxy.get<Rbac[]>(this.configService.apiUrl + 'rbac')
      .pipe(tap(rbaccFiles => this.rbacItems = rbaccFiles));
  }

  public createRbacFile(file: Rbac): Observable<Rbac> {
    return this.apiProxy.post<Rbac>(this.configService.apiUrl + 'rbac', file)
      .pipe(tap(createdRbacc => this.rbacItems.push(createdRbacc)));
  }

  public AddRbac(file: Rbac): Observable<Rbac> {
    return this.apiProxy.post<Rbac>(this.configService.apiUrl + 'rbac', file);
  }

  public AddRbacToLocalCache(file: Rbac) {
    if (this.rbacItems.findIndex(f => f.rbacGuid === file.rbacGuid) > 0) {
      return;
    }
    this.rbacItems.push(file);
  }

  public createGenConnection(rbacId: string, genId: number): Observable<RbacGeneration> {
    return this.apiProxy.post<RbacGeneration>(this.configService.apiUrl + 'rbac/connectGeneration/' + rbacId + '/' + genId, undefined);
  }

  public createSpecConnection(rbacId: string, specId: number): Observable<RbacSpecificationVersion> {
    return this.apiProxy.post<RbacSpecificationVersion>(this.configService.apiUrl + 'rbac/connectSpecification/' + rbacId + '/' + specId, undefined);
  }

  public getByRbacGuid(rbacId: string): Rbac {
    if (this.hasFecthedRbaccFiles) {
      const rbaccFile = this.rbacItems.find(rbaccItem => rbaccItem.rbacGuid === rbacId);
      return rbaccFile;
    }
  }

  public getRbacFromAPI(rbacId: string, baseUrl?: string): Observable<Rbac> {
    return this.apiProxy.get<Rbac>(`${baseUrl || this.configService.apiUrl}rbac/${rbacId}`);
  }

  public getRbacBySpecificationId(specificationId: number, baseUrl?: string): Observable<Rbac> {
    return this.apiProxy.get<Rbac>(`${baseUrl || this.configService.apiUrl}rbac/specificationId/${specificationId}`);
  }

  public getDefaultEntityRbac(familyName: string, generationName: string, baseUrl?: string): Observable<Rbac> {
    return this.apiProxy.get<Rbac>(`${baseUrl || this.configService.apiUrl}rbac/defaultEntity/${familyName}/${generationName}`);
  }

  public getRbaccFileAsXml(rbacId: string, baseUrl?: string): Observable<string> {
    return this.apiProxy.getString((baseUrl || this.configService.apiUrl) + 'rbac/download/' + rbacId);
  }

  public getRbaccFileAsBlob(rbacId: string, baseUrl?: string): Observable<Blob> {
    return this.apiProxy.getBlob((baseUrl || this.configService.apiUrl) + 'rbac/download/' + rbacId);
  }

  public deleteGenConnection(genId: number): Observable<RbacGeneration> {
    return this.apiProxy.delete<RbacGeneration>(this.configService.apiUrl + 'rbac/disconnectGeneration/' + genId);
  }
  public deleteSpecConnection(specId: number): Observable<RbacSpecificationVersion> {
    return this.apiProxy.delete<RbacSpecificationVersion>(this.configService.apiUrl + 'rbac/disconnectSpecificationVersion/' + specId);
  }

  public validateRbacFile(rbacFile: { fileName: string, fileData: File }): Observable<{ isValidFile: boolean, isValidContent: boolean, validationMessage: string }> {
    if (!this.fileService.isCompatibleRbacFile(rbacFile.fileName)) {
      return of({ isValidFile: false, isValidContent: true, validationMessage: `The selected file is not a valid Rbacc file. Only files with extension.xml are allowed` })
    }
    return of({ isValidFile: true, isValidContent: true, validationMessage: `` })

  }

  public getRbacVersionGuid(guid: string): Observable<Rbac> {
    let data = this.apiProxy.get<Rbac>(this.configService.apiUrl + 'rbac/versionGuid/' + guid).pipe(
      tap((res) => console.log('Succesfull 1st service result', res)),
      catchError((error) => {
        if (error) {
          return of(undefined);
        } else {
          throw error;
        }
      }))
    return data;
  }

  public UpdateRbac(file: Rbac): Observable<Rbac> {
    return this.apiProxy.put<Rbac>(this.configService.apiUrl + 'rbac', file);
  }


  public SubmitRbacForReview(rbacGuid: string): Observable<Rbac> {
    return this.apiProxy.put(`${this.configService.apiUrl}rbac/submitForReview/${rbacGuid}`, rbacGuid);
  }

  public ApproveRbacc(rbacGuid: string): Observable<Rbac> {
    return this.apiProxy.put(`${this.configService.apiUrl}rbac/approve/${rbacGuid}`, rbacGuid);
  }

  public RejectRbacc(rbacGuid: string): Observable<Rbac> {
    return this.apiProxy.put(`${this.configService.apiUrl}rbac/reject/${rbacGuid}`, rbacGuid);
  }
}
